import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "images/icons/close-outline.inline.svg";

import ContactForm from "../../components/forms/contact";
import Newsletter from "../../components/newsletter";
import ProgramInfo from "../../components/program/ingo/programInfo";
import ProgramSummary from "../../components/program/programSummary";
import RelatedPrograms from "../../components/program/relatedPrograms";
import TravelFlexibility from "../../components/program/travelFlexibility";
import EnquireCard from "../../components/shared/enquireCard";
import { FullwidthBannerBGImage } from "../../components/shared/fullwidthBannerBGImage";
import MobileGallery from "../../components/shared/gallery/mobileGallery";
import Hero from "../../components/shared/hero";
import Modal from "../../components/shared/modal";
import ModalInPortal from "../../components/shared/modals/modalInPortal";
import { useBannerDetails } from "../../hooks/useBannerManager";
import { useProgramFees } from "../../hooks/useProgramFees";
import { useProgramTestimonials } from "../../hooks/useProgramTestimonials";
import { flattenMenu } from "../../utils/flattenMenu";
import {
  formatHeroBackgroundOptions,
  shortcodePreProcessor
} from "../../utils/formatContent";
import ProgramAccommodation from "./ingo/programAccommodation";
import ProgramDateDurations from "./ingo/programDateDurations";
import ProgramTestimonials from "./ingo/programTestimonials";

const JourneysProgramPageComponent = ({ data, location, theme }) => {
  const { currentSiteMeta } = theme;
  const containerRef = useRef(null);
  const stickyRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const toppoint = containerRef.current?.getBoundingClientRect().top;
      const bottompoint = containerRef.current?.getBoundingClientRect().bottom;
      const cardTop = (window.innerHeight - 410) / 2;
      const cardHeight = 410;
      if (stickyRef.current) {
        if (toppoint > cardTop) {
          stickyRef.current.style.top = 0;
          stickyRef.current.style.bottom = "auto";
        } else if (bottompoint < cardHeight + cardTop) {
          stickyRef.current.style.top = "auto";
          stickyRef.current.style.bottom = 0;
        } else {
          stickyRef.current.style.top = cardTop - toppoint + "px";
          stickyRef.current.style.bottom = "auto";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyRef, containerRef]);

  const [openParentModal, setOpenParentModal] = useState(false);
  const [openBrochureDownloadModal, setBrochureDownloadModal] = useState(false);
  const [getActiveTabSlidebySlug, setActiveTabSlidebySlug] = useState(null);

  const program = data?.program ?? {};
  const programDetails = program?.acfProgramsMeta ?? {};
  const programGeographicals = program?.geographical ?? [];
  const sharedDetails = data?.genericDetails ?? [];
  const ingoPrograms = sharedDetails?.ingoPrograms?.ingoPrograms;
  const programName = program?.title ?? "";
  const programId = program?.databaseId ?? "";
  const programCode = programDetails?.programCode ?? programId;
  const ingoName = programDetails?.ingoBioName ?? "";

  const programIdentifier = programCode ?? programId;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  const applyNow = programId
    ? `/application-form/?${programSuffix}&journey_program&flex_name=${ingoName}`
    : `/application-form/?journey_program&flex_name=${ingoName}`;
  const enquireNow = programId
    ? `/enquiry-form/?type=specific_enquiry&program=${programId}&program_title=${programName}&journey_program`
    : "/enquiry-form/?journey_program";

  const programTitle = programDetails?.programTitle2 ?? "";
  const programSubTitle = programDetails?.programSubtitle ?? "";
  const programPopular = programDetails?.programPopular ?? false;
  const programCapacity = programDetails?.programCapacity ?? "";
  const programDuration = programDetails?.durationDisplay ?? "";
  const programFocus = programDetails?.programFocuses ?? [];
  const programFeaturedVideo = programDetails?.projectVideo ?? "";
  const programFeaturedVideoTestimonials =
    programDetails?.programFeaturedVideoTestimonials ?? "";
  const accommodationGallery = programDetails?.ingoAccommodationGallery ?? [];
  const accommodationSectionDesc =
    programDetails?.ingoAccommodationSectionDesc ?? "";
  const accommodationSectionTitle =
    programDetails?.ingoAccommodationSectionTitle ?? "";
  const programHeading =
    programDetails?.programHeaderOverlayTitle ?? "Emotive headline here";

  const programHeaderSubsectionTitle =
    programDetails?.programHeaderSubsectionTitle ?? "";
  const programHeaderSubsectionSubtitle =
    programDetails?.programHeaderSubsectionSubtitle ?? "";
  const programHeaderSubsectionIcon =
    programDetails?.programHeaderSubsectionIcon ?? {};

  const genBrandMessageTitle = programDetails?.journeysBrandMessageTitle ?? "";
  const genBrandMessageSubtitle =
    programDetails?.journeysBrandMessageDesc ?? "";
  const genBrandMessageImage = programDetails?.journeysBrandMessageImage ?? "";
  const genBrandMessageCTATitle =
    programDetails?.journeysBrandMessageCtaTitle ?? "Enquire now";

  const mobileVideoGallery = [];
  if (
    programFeaturedVideoTestimonials &&
    programFeaturedVideoTestimonials.length > 0
  ) {
    programFeaturedVideoTestimonials.forEach((videos) => {
      mobileVideoGallery.push({
        genVideoUrl: videos?.programFeaturedVideo
      });
    });
  }

  const heroOpts = formatHeroBackgroundOptions(programDetails);

  const programFeaturedImage = heroOpts?.image?.desktop;
  const programFeaturedMobileImage = heroOpts?.image?.mobile;
  const programGallery =
    programDetails?.gallery ?? program?.gallery?.nodes ?? [];

  const programGalleryFeaturedImage =
    program?.featuredImage?.node?.sourceUrl ?? "";
  //filter out svg files from the images
  const filterGalleryCollection = programGallery?.filter(
    (item) =>
      !(item.localFile?.extension === "svg") &&
      !(item.localFile?.extension === "") &&
      item.id !== programFeaturedImage.id &&
      item.id !== program?.featuredImage?.node?.id
  );

  const filteredGallery = programFeaturedVideo
    ? filterGalleryCollection
    : filterGalleryCollection.slice(1);

  const programDatabaseID = program.databaseId;
  const programTestimonials =
    useProgramTestimonials({ programId: programDatabaseID }) ?? [];

  const programTypes = programDetails.programTypes?.flatMap(
    (item) => item.slug
  );

  const programTypesFull = programDetails.programTypes;

  const displayProgramType =
    programTypesFull
      ?.flatMap((item) => item.title)
      ?.shift()
      ?.split(" ")
      ?.join(" <br/>") || "";

  const flatGeoArr = programGeographicals?.nodes.map((geo) => geo);
  const nestedGeo = flattenMenu(flatGeoArr, {
    idKey: "id",
    parentKey: "parentId",
    childrenKey: "children"
  });
  const programHub = nestedGeo
    ?.flatMap((item) => item?.children?.flatMap((child) => child?.children))
    .shift();
  const geographicalNavLinks = [];
  if (nestedGeo && nestedGeo.length) {
    nestedGeo.forEach((geographical) => {
      geographicalNavLinks.push({
        imgPath: "location-icon.svg",
        toPage: geographical?.geographicalTaxonomy?.pageLink?.uri ?? "#",
        text: `GVI ${geographical.name}`
      });

      geographical.children.forEach((childG) => {
        geographicalNavLinks.push({
          imgPath: null,
          toPage: childG?.geographicalTaxonomy?.pageLink?.uri ?? "#",
          text: childG.name
        });

        childG.children.forEach((gChild) => {
          geographicalNavLinks.push({
            imgPath: null,
            toPage: gChild?.geographicalTaxonomy?.pageLink?.uri ?? "#",
            text: gChild.name
          });
        });
      });
    });
  }

  const relatedGeoSlugs = flatGeoArr?.flatMap((geo) => geo.slug);

  const heroNavLinks = geographicalNavLinks;

  const programUri = program.databaseId;
  const programSlug = program?.slug ?? program?.uri;
  const keyMessageIcons =
    sharedDetails?.globalProgramConfig?.acfKeyMessagingIcons?.kmiPrograms ?? [];
  const experiences =
    programHub?.acfCountryExploration?.gviExperiences?.filter((item) => item) ??
    [];

  const { journeysGlobalSupportFeeDesc, journeysGlobalFundsSpent } =
    ingoPrograms;

  const genericConfig =
    sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig || {};

  const { bookWithConfidenceText, bookWithConfidenceTitle } =
    genericConfig ?? {};

  const {
    title: stickyTitle,
    image: stickyImage,
    desc: stickyDesc
  } = useBannerDetails();

  const enquireCardDetails = {
    cardTitle: null,
    enquireCardImg: stickyImage,
    cardDesc:
      `<div>${stickyTitle || ""}</div><div class='font-normal font-body text-sm pt-4'>${stickyDesc || ""}</div>` ??
      "",
    cardBookDesc: null, //stickyBookDesc ?? "Book by"
    shareButton: false,
    language: theme?.localeLanguage
  };

  const travelDetails = {
    title: bookWithConfidenceTitle ?? "Book with confidence",
    description:
      bookWithConfidenceText ??
      `Modify your booking, even up to 48 hours before departure. <a class='underline inline-block' href='/terms-and-conditions' rel='nofollow' target='_gviblank'>T&Cs apply.</a>`
  };

  const parentFormType = "parent_request";
  const parentFormHeading =
    genericConfig?.parentConsultationHeading ?? "Free parent consultation";
  const parentFormSubheading =
    genericConfig?.parentConsultationSubheading ?? "";

  const ingoProviderBio = {
    name: programDetails?.ingoBioName ?? "",
    desc: programDetails?.ingoBioDesc ?? "",
    avatar:
      programDetails?.ingoBioAvatar?.localFile?.publicURL ??
      programDetails?.ingoBioAvatar?.sourceUrl,
    experience: programDetails?.ingoBioExperience ?? ""
  };

  const { ingoPriceDurationsSectionTitle, ingoPriceDurationsSectionSubtext } =
    programDetails ?? {};

  const programFees = useProgramFees() || [];

  const programPriceDescription = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description:
      journeysGlobalSupportFeeDesc ||
      "<span>Secure your spot with a SHORTCODE_ProgramDepositFee deposit</span> and enjoy interest-free instalments on the remaining fee."
  });

  const brochureDownloadFormHeading = "Enter your details <br/> to download";
  const brochureDownloadFormType = "itinerary_form";
  const brochureDownloadFormHiddenFields = [
    { fieldName: "program_id", fieldValue: programCode }
  ];

  const handleCloseModal = () => {
    setBrochureDownloadModal(false);
  };

  const programItineraryFile =
    programDetails?.journeysItineraryFile?.mediaItemUrl ||
    programDetails?.programItineraryFile?.mediaItemUrl ||
    null;

  return (
    <div
      className={"planet:gvi-dark-gray-bg bg-primary-bg flex flex-col sm:block"}
    >
      <Hero
        navLinks={heroNavLinks}
        heroHeadLine={programHeading}
        heroImg={programFeaturedImage}
        heroImageMobile={programFeaturedMobileImage}
        heroClass="hero-section flex-col hidden sm:flex"
        heroHeadLineContainerClass="flex flex-col justify-left pl-6"
        heroHeadLineClass="w-10/12 md:w-full mx-auto text-left text-white uppercase headline-shadow"
        heroNavContainerClass="flex flex-row flex-wrap items-left justify-left w-10/12 md:w-full pt-4 pl-6"
        containerExtraClass="md:!w-10/12 border-l-10 border-l-red-light"
        subtextTitle={displayProgramType}
        subtextDescClass="bg-red-light planet:bg-gray-darker pl-4 pr-4 pb-2 pt-2 font-extrabold text-white text-base uppercase w-fit tracking-widest sm:no-br"
        subSectionTitle={programHeaderSubsectionTitle}
        subSectionDescription={programHeaderSubsectionSubtitle}
        subSectionIcon={programHeaderSubsectionIcon}
        layout="journeys"
      />
      <MobileGallery
        containerClass={
          "ingo-mobile-gallery mobile-gallery-section relative bg-transparent h-56 w-full sm:hidden -order-1"
        }
        gallery={programGallery}
        featuredSingleVideo={programFeaturedVideo}
        videoGallery={mobileVideoGallery}
        programGalleryFeaturedImage={""}
      />
      <ProgramSummary
        programName={programName}
        programTitle={programTitle}
        programSubTitle={programSubTitle}
        programDuration={programDuration}
        programFocus={programFocus}
        programFeaturedVideo={programFeaturedVideo}
        programFeaturedImage={programFeaturedImage}
        programGalleryFeaturedImage={programGalleryFeaturedImage}
        programProviderBio={ingoProviderBio}
        programUri={programUri}
        programId={programId}
        programCode={programCode}
        gallery={filteredGallery}
        programPopular={programPopular}
        programCapacity={programCapacity}
        programLocation={geographicalNavLinks}
        programFeaturedVideoTestimonials={programFeaturedVideoTestimonials}
        programType={programTypes}
        programTypes={programDetails.programTypes}
        setOpenModal={setOpenParentModal}
        setBrochureDownloadModal={setBrochureDownloadModal}
        openModal={openParentModal}
        formType={parentFormType}
        formSubheading={parentFormSubheading}
        formHeading={parentFormHeading}
        setActiveTabSlidebySlug={setActiveTabSlidebySlug}
        galleryCTALabel={"See gallery"}
        layout={"journeys"}
        fundSpendingStatusText={journeysGlobalFundsSpent}
        containerClass={
          "summary-section flex flex-col items-center justify-center bg-[#E8EDEB] mx-auto sm:pb-18 pb-8 pt-8 px-2 relative"
        }
        innerDetailContainerClass={
          "inner-detail-container justify-between md:items-center mt-4 pt-3 sm:my-4 sm:py-0 border-t border-gray-generic planet:border-gray-standard sm:border-0"
        }
        bgIllustrationClass={
          "absolute z-0 hidden -bottom-80 -left-16 md:block stroke-white bg-[#E8EDEB] w-1/3"
        }
        innerContainer={"container flex flex-col w-11/12 sm:w-10/12"}
        mainHeadingClass={"font-normal sm:planet:font-semibold"}
        mainHeadingContainerClass={
          "flex flex-col items-left justify-between w-full"
        }
        subHeadingClass={"py-2 font-light leading-6 sm:leading-[38px]"}
        galleryContainerClass={
          "hidden sm:grid grid-flow-col grid-cols-4 grid-rows-2 gap-0 program-gallery sm:h-96 2xl:h-auto sm:gap-4"
        }
        galleryViewMoreContainerClass={
          "hidden sm:flex flex-row justify-end py-4 sm:absolute sm:bottom-3 sm:right-3"
        }
        featuredVideoClass={"bg-gray-dark"}
        featuredGalleryItemClass={"planet:bg-gray-dark"}
      />
      <div className="relative overflow-x-hidden sticky-wrapper sm:overflow-x-clip ">
        <ProgramInfo
          programTitle={programName}
          programDuration={programDuration}
          programDetails={programDetails}
          providerBiographyDetail={ingoProviderBio}
          excerpt={program?.content}
          programLocation={geographicalNavLinks}
          sharedDetails={sharedDetails}
          testimonials={programTestimonials}
          program={program}
          keyMessageIcons={keyMessageIcons}
          experiences={experiences}
          programHub={programHub}
          programPopular={programPopular}
          programCapacity={programCapacity}
          travelDetails={travelDetails}
          location={location}
          setActiveTabSlidebySlug={setActiveTabSlidebySlug}
          getActiveTabSlidebySlug={getActiveTabSlidebySlug}
          setBrochureDownloadModal={setBrochureDownloadModal}
          brochureDownloadFile={programItineraryFile}
          layout={"journeys"}
          innerContainerClass="summary-section justify-center w-11/12 sm:pt-12 sm:pb-5 flex gap-10 flex-wrap sm:flex-nowrap sm:w-10/12 2xl:container 2xl:m-auto"
          innerContainerChildClass="w-full lg:sticky-left-section mr-auto lg:!pr-4"
          outerContainerClass="w-full bg-primary-bg mx-auto flex items-center justify-center relative overflow-hidden"
          programAccommodationGallery={
            <ProgramAccommodation
              gallery={accommodationGallery}
              description={accommodationSectionDesc}
              title={accommodationSectionTitle}
              innerContainerClass="w-full"
              outerContainerClass="w-full"
              innerContainerChildClass="w-full border-t border-gray-generic planet:border-gray-standard planet:md:border-gray-light"
              titleContainer="pt-10 md:pt-12"
            />
          }
        />

        <ProgramDateDurations
          programCode={programCode}
          title={ingoPriceDurationsSectionTitle}
          travelDesc={ingoPriceDurationsSectionSubtext}
          description={programPriceDescription}
          applicationUrl={applyNow}
          fromPrefix=" from "
          showdayPrice={false}
          showDatesSummary={true}
          innerContainerChildClass="w-full lg:sticky-left-section mr-auto lg:!pr-4 border-t border-gray-generic planet:border-gray-standard planet:md:border-gray-light"
        />

        <div className="w-full px-4 block sm:hidden -mt-6 pb-6 ">
          <TravelFlexibility
            travelDetails={travelDetails}
            isGVI={true}
            hasBullets={false}
            outerContainerClass="sm:h-full my-6 "
            innerContainerClass="py-6 border-t border-gray-generic rounded-none"
            headingClass="text-xl font-semibold font-body text-black-lighter planet:text-white pb-3"
            listItemClass="text-xs"
          />
        </div>

        <div className="hidden w-full md:absolute md:h-full md:sticky-position-programs md:w-auto lg:flex">
          <div className="relative w-side-sticky-widget" ref={containerRef}>
            <div ref={stickyRef} className={`card-sticky-container absolute`}>
              <EnquireCard
                applyNow={applyNow}
                enquireNow={enquireNow}
                enquireCardDetails={enquireCardDetails}
                isGVI={true}
                descClass={
                  "mb-4 text-xl font-alice font-light text-left text-generic-text planet:text-xl planet:font-light planet:font-body hover:text-gray-900 leading-6"
                }
              />

              <TravelFlexibility
                travelDetails={travelDetails}
                isGVI={true}
                hasBullets={false}
                outerContainerClass="border-0.5 sm:h-full my-6 bg-yellow-tertiary planet:bg-generic-bg"
                innerContainerClass="p-6"
                headingClass="sm:text-xl sm:font-semibold font-body text-black-lighter planet:text-white pb-3"
                listItemClass="text-xs"
              />
            </div>
          </div>
        </div>
      </div>

      <FullwidthBannerBGImage
        bannerTitle={genBrandMessageTitle}
        bannerSubTitle={genBrandMessageSubtitle}
        bannerTitleClass="font-light text-white planet:font-bold text-xl md:text-xlrg-sm md:max-w-md"
        subTitleClass="mt-2 md:mt-4 text-md text-white font-normal md:max-w-md"
        imageSrc={genBrandMessageImage}
        showCTAButton={true}
        ctaLabel={genBrandMessageCTATitle}
        ctaUri={enquireNow}
        ctaPreset="senary"
        ctaContainerClass="w-fill sm:w-1/5 pt-10"
        bgImageDefaultClass="absolute planet:relative planet:sm:absolute z-0 top-0 right-0 left-0 bottom-0 xxxs:h-[120px] sm:h-full"
        containerClass="mx-auto xxxs:w-11/12 sm:w-full sm:px-4 sm:py-12 bg-black-lighter h-96 planet:h-full sm:h-104 planet:sm:h-104 rounded-md overflow-hidden sm:rounded-none"
        bannerOuterContainerClass="relative z-20 flex justify-center items-end sm:items-center planet:items-center px-7 sm:px-0 mt-10 sm:mt-0 mb-10 sm:mb-0 w-full h-full"
        gradientColors="from-black-lighter via-black-lighter via-20% to-60% sm:to-100%  sm:from-black-lighter sm:via-black-lighter sm:planet:from-gray-darker sm:planet:via-gray-darker"
        gradientDefaultClass="bg-gradient-to-r sm:bg-gradient-to-r top-0 xxxs:h-[120px] sm:h-full sm:bottom-0 planet:h-full w-full sm:w-3/5 absolute left-0 z-10 sm:block"
      />

      <ProgramTestimonials TestimonialProgramCode={programCode} />

      <RelatedPrograms
        relatedGeoSlugs={relatedGeoSlugs}
        innerContainerClass={"sm:bg-white sm:planet:bg-gray-standard"}
        excludeSlug={programSlug}
      />

      <Newsletter headerCol="sm:bg-gray-tertiary planet:sm:bg-gray-standard" />

      {openBrochureDownloadModal && (
        <>
          <ModalInPortal
            openModal={openBrochureDownloadModal}
            modal={
              <Modal
                closeModal={handleCloseModal}
                modalCloseIcon={
                  <CloseIcon className="h-5 fill-white planet:fill-white" />
                }
                modalContentStyles="w-full mx-auto bg-yellow-standard md:blur-none planet:gvi-blur-newsletter-bg planet:gvi-blur-bg gvi-border-light border-[0.5px] border-gray-testimonial rounded-[3px] py-3 z-10"
                modalContainerStyles="w-full sm:w-3/5 h-full sm:h-auto"
                modalOuterContainerStyles={
                  "fixed top-0 left-0 right-0 overflow-scroll z-20 flex items-center justify-center w-full h-full sm:modalBackground bg-white planet:bg-gray-darkest sm:gvi-modal-light-bg planet:sm:bg-gray-darker/80"
                }
                modalCloseBtnStyles="sm:hidden px-4 pt-4 sm:hidden"
                modalContent={
                  <div className="px-4 sm:px-16 pt-4 pb-8">
                    <ContactForm
                      formType={brochureDownloadFormType}
                      formHeading={brochureDownloadFormHeading}
                      formStyles="rounded-md w-full mx-auto"
                      ctaLabel="Download"
                      hiddenFields={brochureDownloadFormHiddenFields}
                      programItineraryFile={programItineraryFile}
                      downloadTitle={"Itinerary"}
                      formTooltipIconStyles={"fill-white"}
                      buttonClass={
                        "w-full sm:w-1/2 callback-button bg-white planet:gvi-bg-green planet:hover:gvi-bg-green planet:!text-gray-darker planet:border-green-standard planet:hover:border-green-standard"
                      }
                      iconColor={"fill-white stroke-white"}
                      calendlyActive={false}
                      customFormType={"journeys_brochures_form"}
                    />
                  </div>
                }
              />
            }
          ></ModalInPortal>
        </>
      )}
    </div>
  );
};

export default JourneysProgramPageComponent;
